import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Block } from '../../types/Study';
import { RootState } from '../../store/store';
import { createEntityAdapter } from '@reduxjs/toolkit';

const blocksAdapter = createEntityAdapter<Block>({
  sortComparer: (a, b) => (a.ord || 0) - (b.ord || 0),
});

const initialState = blocksAdapter.getInitialState();

const blocksSlice = createSlice({
  name: 'blocks',
  initialState,
  reducers: {
    blocksLoaded: (state, action: PayloadAction<Block[]>) => {
      blocksAdapter.setAll(state, action.payload);
    },
    blockReceived: (state, action: PayloadAction<Block>) => {
      blocksAdapter.upsertOne(state, action.payload);
    },
    blockUpdated: (state, action: PayloadAction<Block>) => {
      blocksAdapter.updateOne(state, {
        id: action.payload.id,
        changes: action.payload,
      });
    },
    blocksCleared: (state) => {
      blocksAdapter.removeAll(state);
    },
    blockAnswerUpdated: (
      state,
      action: PayloadAction<{
        blockId: string;
        vignetteId: string;
        answerData: Partial<{
          answer: string;
          correctAnswer: string;
          submitted: boolean;
          vignetteContentId: string;
          secondAnswer: string;
          secondSubmitted: boolean;
        }>;
      }>
    ) => {
      const { blockId, vignetteId, answerData } = action.payload;
      const existingBlock = state.entities[blockId];

      if (existingBlock) {
        // Initialize answers object if it doesn't exist
        if (!existingBlock.answers) {
          existingBlock.answers = {};
        }

        // Update or create the answer entry
        existingBlock.answers[vignetteId] = {
          ...existingBlock.answers[vignetteId],
          ...answerData,
        };
      }
    },
  },
});

// Create adapter selectors
const blocksSelectors = blocksAdapter.getSelectors(
  (state: RootState) => state.blocks || blocksAdapter.getInitialState()
);

export const selectBlocks = blocksSelectors.selectAll;

export const selectSubmittedBlocks = createSelector(
  [blocksSelectors.selectAll],
  (blocks: Block[]) => {
    return blocks.filter((block: Block) => block.isSubmitted);
  }
);

export const selectActiveBlockId = createSelector(
  [
    (state: RootState) => state.todaySession.session?.activeBlockId ?? '',
    (state: RootState) => state.studyContext,
  ],
  (sessionActiveBlockId, studyContext) => {
    if (studyContext.mode === 'standalone') {
      return studyContext.activeBlockId ?? '';
    }
    return sessionActiveBlockId;
  }
);

export const selectActiveBlock = createSelector(
  [blocksSelectors.selectAll, selectActiveBlockId],
  (blocks: Block[], activeBlockId: string) => {
    return blocks.find((block: Block) => block.id === activeBlockId) || null;
  }
);

export const selectUnstartedBlocks = createSelector(
  [blocksSelectors.selectAll],
  (blocks: Block[]) => {
    return blocks.filter((block: Block) => !block.isStarted);
  }
);

export const selectUnsubmittedBlocks = createSelector(
  [blocksSelectors.selectAll],
  (blocks: Block[]) => {
    return blocks.filter((block: Block) => !block.isSubmitted);
  }
);

export const selectStartedVignettesCount = createSelector(
  [blocksSelectors.selectAll],
  (blocks: Block[]) => {
    return blocks.reduce((count: number, block: Block) => {
      if (block.isStarted) {
        return count + block.vignetteCount;
      }
      return count;
    }, 0);
  }
);

export const selectAllBlocksSubmitted = createSelector(
  [blocksSelectors.selectAll],
  (blocks: Block[]) => {
    return (
      blocks.length > 0 && blocks.every((block: Block) => block.isSubmitted)
    );
  }
);

export const selectIsOneBlockSession = createSelector(
  [(state: RootState) => state.todaySession.session?.blockCount],
  (blockCount: number) => {
    return blockCount === 1;
  }
);

export const selectNextActiveBlock = createSelector(
  [selectActiveBlock, selectUnsubmittedBlocks, selectSubmittedBlocks],
  (
    activeBlock: Block | null,
    unsubmittedBlocks: Block[],
    submittedBlocks: Block[]
  ) => {
    // Return current active block if it's unsubmitted
    if (activeBlock && !activeBlock.isSubmitted) {
      return activeBlock;
    }

    // Return first unsubmitted block
    if (unsubmittedBlocks.length > 0) {
      return unsubmittedBlocks[0];
    }

    // Return first submitted block
    if (submittedBlocks.length > 0) {
      return submittedBlocks[0];
    }

    return null;
  }
);

export const {
  blocksLoaded,
  blockReceived,
  blockUpdated,
  blocksCleared,
  blockAnswerUpdated,
} = blocksSlice.actions;

export default blocksSlice.reducer;
